import {
  Box,
  Button,
  Group,
  Input,
  InputAddon,
  NativeSelect,
  Separator,
  Text,
} from '@chakra-ui/react'
import type { CountryCode } from 'libphonenumber-js'
import { getCountryCallingCode, parsePhoneNumber } from 'libphonenumber-js'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  Checkbox,
  Field,
  Modal,
  StateProvinceCombobox,
  Toggle,
} from '@mangovoice/mango-ui'

import { getPhoneNumberTypeSelectOptions } from '../constants'
import type { PhoneNumbersTranslateFn } from '../types/i18n.types'
import { text } from '../types/i18n.types'
import type {
  PhoneNumberCreatePayload,
  SelectOption,
} from '../types/phoneNumber.types'
import { validateCreateFields } from '../utils/validation'
import type { CreateValidationErrors } from '../utils/validation'
import { IntegrationCombobox } from './IntegrationCombobox'

const LOCAL_PRESENCE_OPTIONS = [
  { value: 0, label: 'Off' },
  { value: 1, label: 'Group 1' },
  { value: 2, label: 'Group 2' },
  { value: 3, label: 'Group 3' },
  { value: 4, label: 'Group 4' },
  { value: 5, label: 'Group 5' },
  { value: 6, label: 'Group 6' },
]

export interface CreatePhoneNumberModalProps {
  pbxId: number
  open: boolean
  onClose: () => void
  onSave: (payload: PhoneNumberCreatePayload) => Promise<void>
  onSaveSuccess?: () => void
  isSaving?: boolean
  t?: PhoneNumbersTranslateFn
  statuses?: SelectOption[]
  providers?: SelectOption[]
  extensions?: SelectOption[]
  faxMachines?: SelectOption[]
  integrations?: SelectOption[]
  /** ISO 3166-1 alpha-2 country code (e.g. 'US', 'GB') used to pre-fill the dial code (+1, +44). */
  defaultCountryCode?: CountryCode
  /** Read-only PBX server group name. Staff-only: omit for customer-facing hosts. */
  serverGroup?: string
}

export const CreatePhoneNumberModal: React.FC<CreatePhoneNumberModalProps> = ({
  pbxId,
  open,
  onClose,
  onSave,
  onSaveSuccess,
  isSaving,
  t,
  statuses,
  providers,
  faxMachines,
  integrations,
  defaultCountryCode,
  serverGroup,
}) => {
  const defaultDialPrefix = useMemo(
    () =>
      defaultCountryCode ? `+${getCountryCallingCode(defaultCountryCode)}` : '',
    [defaultCountryCode],
  )

  const [number, setNumber] = useState('')
  const [name, setName] = useState('')
  const [type, setType] = useState('')
  const [statusId, setStatusId] = useState('')
  const [voiceProviderId, setVoiceProviderId] = useState('')
  const [smsProviderId, setSmsProviderId] = useState('')
  const [callerIdentification, setCallerIdentification] = useState('')
  const [faxExtensionId, setFaxExtensionId] = useState('')
  const [sourceTracking, setSourceTracking] = useState('')
  const [localPresence, setLocalPresence] = useState(0)
  const [campaignId, setCampaignId] = useState('')
  const [smsEnabled, setSmsEnabled] = useState(false)
  const [smsRelay, setSmsRelay] = useState(false)
  const [integrationId, setIntegrationId] = useState('')
  const [e911Address, setE911Address] = useState('')
  const [e911Address2, setE911Address2] = useState('')
  const [e911City, setE911City] = useState('')
  const [e911State, setE911State] = useState('')
  const [e911PostalCode, setE911PostalCode] = useState('')

  const [touched, setTouched] = useState(false)
  const [blurredFields, setBlurredFields] = useState<
    Partial<Record<keyof CreateValidationErrors, boolean>>
  >({})

  const [createMore, setCreateMore] = useState(false)
  const numberInputRef = useRef<HTMLInputElement>(null)

  const resetForm = useCallback(() => {
    setNumber('')
    setName('')
    setType('')
    setStatusId('')
    setVoiceProviderId('')
    setSmsProviderId('')
    setCallerIdentification('')
    setFaxExtensionId('')
    setSourceTracking('')
    setLocalPresence(0)
    setCampaignId('')
    setSmsEnabled(false)
    setSmsRelay(false)
    setIntegrationId('')
    setE911Address('')
    setE911Address2('')
    setE911City('')
    setE911State('')
    setE911PostalCode('')
    setTouched(false)
    setBlurredFields({})
  }, [])

  const partialReset = useCallback(() => {
    setNumber('')
    setName('')
    setE911Address('')
    setE911Address2('')
    setE911City('')
    setE911State('')
    setE911PostalCode('')
    setTouched(false)
    setBlurredFields({})
  }, [])

  const fullNumber = (() => {
    const trimmed = number.trim()
    if (!defaultDialPrefix) return number
    if (!trimmed) return ''
    return `${defaultDialPrefix}${trimmed}`
  })()

  const hasProviders = !!providers && providers.length > 0
  const unassignedProvider = useMemo(
    () => providers?.find((p) => p.name === 'Unassigned'),
    [providers],
  )

  useEffect(() => {
    if (unassignedProvider && !smsProviderId) {
      setSmsProviderId(String(unassignedProvider.id))
    }
  }, [unassignedProvider, smsProviderId, setSmsProviderId])

  const validationErrors = useMemo(
    () =>
      validateCreateFields({
        number: fullNumber,
        name,
        type,
        statusId,
        voiceProviderId,
        hasProviders,
        hasDialPrefix: !!defaultDialPrefix,
        callerIdentification,
        campaignId,
        sourceTracking,
        e911Address,
        e911City,
        e911State,
        e911PostalCode,
        e911Region: defaultCountryCode,
        t,
      }),
    [
      fullNumber,
      name,
      type,
      statusId,
      voiceProviderId,
      hasProviders,
      callerIdentification,
      campaignId,
      sourceTracking,
      e911Address,
      e911City,
      e911State,
      e911PostalCode,
      defaultCountryCode,
      t,
    ],
  )

  const hasErrors = Object.keys(validationErrors).length > 0

  const handleSave = useCallback(async () => {
    setTouched(true)
    if (hasErrors) return

    const parsed = parsePhoneNumber(fullNumber.trim())
    const payload: PhoneNumberCreatePayload = {
      pbx: pbxId,
      number: parsed.number,
      name: name.trim(),
      type,
      status: Number(statusId),
      voice_provider: voiceProviderId ? Number(voiceProviderId) : undefined,
      sms_provider: smsProviderId ? Number(smsProviderId) : undefined,
      caller_identification: callerIdentification || undefined,
      fax_extension: faxExtensionId ? Number(faxExtensionId) : null,
      prepend_string: sourceTracking || undefined,
      local_presence: localPresence,
      campaign_id: campaignId ? campaignId.toUpperCase() : undefined,
      sms_enabled: smsEnabled,
      sms_relay: smsRelay,
      integration: integrationId ? Number(integrationId) : null,
      e911_address: e911Address || undefined,
      e911_address_2: e911Address2 || undefined,
      e911_city: e911City || undefined,
      e911_state: e911State || undefined,
      e911_postal_code: e911PostalCode || undefined,
    }
    try {
      await onSave(payload)
    } catch {
      // The mutation's `onError` already surfaces a toast describing the
      // backend validation failure (e.g. duplicate number). Swallow here so
      // the rejection doesn't bubble out of the click handler as an
      // unhandled rejection (which would otherwise alarm in Sentry on every
      // routine 4xx).
      return
    }
    if (createMore) {
      partialReset()
      requestAnimationFrame(() => numberInputRef.current?.focus())
    } else {
      resetForm()
      onSaveSuccess?.()
    }
  }, [
    hasErrors,
    pbxId,
    fullNumber,
    name,
    type,
    statusId,
    voiceProviderId,
    smsProviderId,
    callerIdentification,
    faxExtensionId,
    sourceTracking,
    localPresence,
    campaignId,
    smsEnabled,
    smsRelay,
    integrationId,
    e911Address,
    e911Address2,
    e911City,
    e911State,
    e911PostalCode,
    onSave,
    createMore,
    partialReset,
    resetForm,
    onSaveSuccess,
  ])

  const handleClose = useCallback(() => {
    resetForm()
    onClose()
  }, [resetForm, onClose])

  const showError = (field: keyof CreateValidationErrors) =>
    (touched || blurredFields[field]) && validationErrors[field]

  const handleBlur = (field: keyof CreateValidationErrors) =>
    setBlurredFields((prev) => ({ ...prev, [field]: true }))

  return (
    <Modal
      open={open}
      onOpenChange={(e) => {
        if (!e.open) handleClose()
      }}
      title={text(t, 'phoneNumbers.create.title', 'Create Phone Number')}
      maxWidth="xl"
      placement="top"
      footer={
        <>
          <Checkbox
            size="sm"
            checked={createMore}
            onCheckedChange={(e) => setCreateMore(!!e.checked)}
          >
            {text(t, 'phoneNumbers.create.createMore', 'Create more')}
          </Checkbox>
          <Button
            variant="outline"
            borderRadius="lg"
            onClick={handleClose}
            disabled={isSaving}
          >
            {text(t, 'phoneNumbers.edit.cancel', 'Cancel')}
          </Button>
          <Button
            colorPalette="brand"
            borderRadius="lg"
            onClick={handleSave}
            disabled={isSaving || (touched && hasErrors)}
            loading={isSaving}
          >
            {text(t, 'phoneNumbers.create.saveButton', 'Create')}
          </Button>
        </>
      }
    >
      <Box display="flex" flexDirection="column" gap={4}>
        {serverGroup && (
          <Text
            fontSize="sm"
            color="fg.muted"
            bg="bg.subtle"
            borderRadius="md"
            px={3}
            py={2}
          >
            {text(t, 'phoneNumbers.list.serverGroup', 'Server Group:')}{' '}
            <Text as="span" fontWeight="semibold" color="fg">
              {serverGroup}
            </Text>
          </Text>
        )}

        {/* -- General fields -- */}
        <Field.Root invalid={!!showError('number')} required>
          <Field.Label>
            {text(t, 'phoneNumbers.create.numberLabel', 'Phone Number')}
          </Field.Label>
          {defaultDialPrefix ? (
            <Group attached>
              <InputAddon>{defaultDialPrefix}</InputAddon>
              <Input
                ref={numberInputRef}
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                onBlur={() => handleBlur('number')}
                placeholder="(801) 555-1234"
              />
            </Group>
          ) : (
            <Input
              ref={numberInputRef}
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              onBlur={() => handleBlur('number')}
              placeholder={text(
                t,
                'phoneNumbers.create.numberPlaceholder',
                '+1XXXXXXXXXX',
              )}
            />
          )}
          {showError('number') ? (
            <Field.ErrorText>{validationErrors.number}</Field.ErrorText>
          ) : (
            <Field.HelperText>
              {defaultDialPrefix
                ? text(
                    t,
                    'phoneNumbers.create.numberHelpWithPrefix',
                    'Enter the number without the country code.',
                  )
                : text(
                    t,
                    'phoneNumbers.create.numberHelp',
                    'Must include country code (e.g. +18015551234 for US, +442079460958 for UK).',
                  )}
            </Field.HelperText>
          )}
        </Field.Root>

        <Field.Root invalid={!!showError('name')} required>
          <Field.Label>
            {text(t, 'phoneNumbers.edit.nameLabel', 'Name')}
          </Field.Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => handleBlur('name')}
            placeholder={text(
              t,
              'phoneNumbers.edit.namePlaceholder',
              'Phone number name',
            )}
          />
          {showError('name') && (
            <Field.ErrorText>{validationErrors.name}</Field.ErrorText>
          )}
        </Field.Root>

        <Box display="flex" gap={3}>
          <Field.Root invalid={!!showError('type')} required flex={1}>
            <Field.Label>
              {text(t, 'phoneNumbers.edit.typeLabel', 'Type')}
            </Field.Label>
            <NativeSelect.Root>
              <NativeSelect.Field
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                <option value="">
                  {text(t, 'phoneNumbers.create.selectType', '— Select Type —')}
                </option>
                {getPhoneNumberTypeSelectOptions(t).map((pt) => (
                  <option key={pt.value} value={pt.value}>
                    {pt.label}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
            {showError('type') && (
              <Field.ErrorText>{validationErrors.type}</Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root invalid={!!showError('status')} required flex={1}>
            <Field.Label>
              {text(t, 'phoneNumbers.edit.statusLabel', 'Status')}
            </Field.Label>
            <NativeSelect.Root>
              <NativeSelect.Field
                value={statusId}
                onChange={(e) => setStatusId(e.target.value)}
              >
                <option value="">
                  {text(
                    t,
                    'phoneNumbers.edit.selectStatus',
                    '— Select Status —',
                  )}
                </option>
                {(statuses ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
            {showError('status') && (
              <Field.ErrorText>{validationErrors.status}</Field.ErrorText>
            )}
          </Field.Root>
        </Box>

        {hasProviders && (
          <Box display="flex" gap={3}>
            <Field.Root
              flex={1}
              required
              invalid={!!showError('voiceProvider')}
            >
              <Field.Label>
                {text(
                  t,
                  'phoneNumbers.edit.voiceProviderLabel',
                  'Voice Provider',
                )}
              </Field.Label>
              <NativeSelect.Root>
                <NativeSelect.Field
                  value={voiceProviderId}
                  onChange={(e) => setVoiceProviderId(e.target.value)}
                >
                  <option value="">
                    {text(t, 'phoneNumbers.edit.selectProvider', '— Select —')}
                  </option>
                  {providers.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
              {showError('voiceProvider') && (
                <Field.ErrorText>
                  {validationErrors.voiceProvider}
                </Field.ErrorText>
              )}
            </Field.Root>

            <Field.Root flex={1}>
              <Field.Label>
                {text(t, 'phoneNumbers.edit.smsProviderLabel', 'SMS Provider')}
              </Field.Label>
              <NativeSelect.Root>
                <NativeSelect.Field
                  value={smsProviderId}
                  onChange={(e) => setSmsProviderId(e.target.value)}
                >
                  {providers.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Field.Root>
          </Box>
        )}

        <Field.Root invalid={!!showError('callerIdentification')}>
          <Field.Label>
            {text(t, 'phoneNumbers.edit.callerIdLabel', 'Caller ID')}
          </Field.Label>
          <Input
            value={callerIdentification}
            onChange={(e) => setCallerIdentification(e.target.value)}
            maxLength={15}
          />
          {showError('callerIdentification') && (
            <Field.ErrorText>
              {validationErrors.callerIdentification}
            </Field.ErrorText>
          )}
        </Field.Root>

        {faxMachines && faxMachines.length > 0 && (
          <Field.Root>
            <Field.Label>
              {text(
                t,
                'phoneNumbers.edit.sendFaxesFromLabel',
                'Send Faxes From',
              )}
            </Field.Label>
            <NativeSelect.Root>
              <NativeSelect.Field
                value={faxExtensionId}
                onChange={(e) => setFaxExtensionId(e.target.value)}
              >
                <option value="">
                  {text(
                    t,
                    'phoneNumbers.edit.usePbxDefault',
                    'Use PBX Default',
                  )}
                </option>
                {faxMachines.map((fm) => (
                  <option key={fm.id} value={fm.id}>
                    {fm.name}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>
        )}

        <Field.Root invalid={!!showError('sourceTracking')}>
          <Field.Label>
            {text(
              t,
              'phoneNumbers.edit.sourceTrackingLabel',
              'Source Tracking',
            )}
          </Field.Label>
          <Input
            value={sourceTracking}
            onChange={(e) => setSourceTracking(e.target.value)}
            maxLength={6}
          />
          {showError('sourceTracking') ? (
            <Field.ErrorText>{validationErrors.sourceTracking}</Field.ErrorText>
          ) : (
            <Field.HelperText>
              {text(
                t,
                'phoneNumbers.edit.sourceTrackingHelp',
                'Prepend up to 6 characters to the Caller ID for inbound calls. This is commonly used to track ad campaigns.',
              )}
            </Field.HelperText>
          )}
        </Field.Root>

        <Field.Root>
          <Field.Label>
            {text(t, 'phoneNumbers.edit.localPresenceLabel', 'Local Presence')}
          </Field.Label>
          <NativeSelect.Root>
            <NativeSelect.Field
              value={localPresence}
              onChange={(e) => setLocalPresence(Number(e.target.value))}
            >
              {LOCAL_PRESENCE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
          <Field.HelperText>
            {text(
              t,
              'phoneNumbers.edit.localPresenceHelp',
              'Local presence groups create a pool of numbers that a local-presence-enabled line extension can place a call from in order to appear local to the callee.',
            )}
          </Field.HelperText>
        </Field.Root>

        <Separator borderColor="border.muted" mt={2} />

        {/* -- E911 section -- */}
        <Text fontWeight="700" fontSize="lg">
          {text(
            t,
            'phoneNumbers.edit.e911Section',
            'Emergency 911 (E911) Settings',
          )}
        </Text>

        {showError('e911') && (
          <Text fontSize="xs" color="fg.error">
            {validationErrors.e911}
          </Text>
        )}

        <Field.Root>
          <Field.Label>
            {text(t, 'phoneNumbers.edit.e911AddressLabel', 'Address')}
          </Field.Label>
          <Input
            value={e911Address}
            onChange={(e) => setE911Address(e.target.value)}
          />
        </Field.Root>

        <Field.Root>
          <Field.Label>
            {text(
              t,
              'phoneNumbers.edit.e911Address2Label',
              'Address apartment/suite',
            )}
          </Field.Label>
          <Input
            value={e911Address2}
            onChange={(e) => setE911Address2(e.target.value)}
          />
        </Field.Root>

        <Box display="flex" gap={3}>
          <Field.Root flex={2}>
            <Field.Label>
              {text(t, 'phoneNumbers.edit.e911CityLabel', 'City')}
            </Field.Label>
            <Input
              value={e911City}
              onChange={(e) => setE911City(e.target.value)}
            />
          </Field.Root>
          <Field.Root flex={1} minW={0}>
            <Field.Label htmlFor="phone-numbers-create-e911-state">
              {text(t, 'phoneNumbers.edit.e911StateLabel', 'State/Province')}
            </Field.Label>
            <StateProvinceCombobox
              variant="phoneNumber"
              value={e911State}
              onValueChange={setE911State}
              allowEmpty
              resetSearchKey={open}
              id="phone-numbers-create-e911-state"
              placeholder={text(
                t,
                'phoneNumbers.create.searchStateProvince',
                'Search...',
              )}
              emptySearchMessage={text(
                t,
                'phoneNumbers.create.noStateProvinceMatches',
                'No matches',
              )}
            />
          </Field.Root>
          <Field.Root flex={1}>
            <Field.Label>
              {text(t, 'phoneNumbers.edit.e911PostalCodeLabel', 'Postal Code')}
            </Field.Label>
            <Input
              value={e911PostalCode}
              onChange={(e) => setE911PostalCode(e.target.value)}
            />
          </Field.Root>
        </Box>

        <Text fontSize="sm" color="fg.muted">
          {text(
            t,
            'phoneNumbers.edit.e911Disclaimer',
            "E911 is a system used in North America to automatically provide the caller's location to 911 dispatchers. Please make sure this information is accurate--someone's life could depend on it.",
          )}
        </Text>

        <Separator borderColor="border.muted" mt={2} />

        {/* -- SMS section -- */}
        <Text fontWeight="700" fontSize="lg">
          {text(t, 'phoneNumbers.edit.smsSection', 'SMS Settings')}
        </Text>

        <Box display="flex" flexDirection="column" gap={3}>
          <Box display="flex" flexDirection={'column'} gap={2}>
            <Toggle
              size="sm"
              colorPalette="brand"
              checked={smsEnabled}
              onCheckedChange={(e) => {
                setSmsEnabled(e.checked)
                if (!e.checked) {
                  setSmsRelay(false)
                  setIntegrationId('')
                }
              }}
              display={'flex'}
            >
              {text(t, 'phoneNumbers.edit.smsEnabledLabel', 'SMS Enabled')}
            </Toggle>

            <Toggle
              size="sm"
              colorPalette="brand"
              checked={smsRelay}
              onCheckedChange={(e) => {
                setSmsRelay(e.checked)
                if (!e.checked) {
                  setIntegrationId('')
                }
              }}
              disabled={!smsEnabled}
              display={'flex'}
            >
              {text(t, 'phoneNumbers.edit.smsRelayLabel', 'SMS Relay')}
            </Toggle>
          </Box>

          {smsEnabled && (
            <Box
              bg="bg.error"
              borderWidth="1px"
              borderColor="error.muted"
              borderRadius="md"
              p={3}
            >
              <Text fontSize="sm" color="red.fg">
                {text(
                  t,
                  'phoneNumbers.edit.smsEnabledWarning',
                  'Warning: enabling SMS will trigger a port-in request of the eSPID on this number, which may cause SMS downtime for the customer. Please make sure this is the desired action before proceeding.',
                )}
              </Text>
            </Box>
          )}
        </Box>

        <Field.Root>
          <Field.Label>
            {text(
              t,
              'phoneNumbers.edit.smsRelayIntegrationLabel',
              'SMS Relay Integration',
            )}
          </Field.Label>
          <IntegrationCombobox
            t={t}
            integrations={integrations ?? []}
            value={integrationId}
            onChange={setIntegrationId}
            disabled={!smsRelay}
          />
          {!smsRelay && (
            <Field.HelperText>
              {text(
                t,
                'phoneNumbers.edit.smsRelayIntegrationDisabledHelp',
                'Enable SMS Relay to choose an integration.',
              )}
            </Field.HelperText>
          )}
        </Field.Root>

        <Field.Root invalid={!!showError('campaignId')}>
          <Field.Label>
            {text(t, 'phoneNumbers.edit.campaignIdLabel', 'Campaign ID')}
          </Field.Label>
          <Input
            value={campaignId}
            onChange={(e) => setCampaignId(e.target.value.toUpperCase())}
            maxLength={7}
          />
          {showError('campaignId') ? (
            <Field.ErrorText>{validationErrors.campaignId}</Field.ErrorText>
          ) : (
            <Field.HelperText>
              {text(
                t,
                'phoneNumbers.edit.campaignIdHelp',
                'Required for sending SMS. Prior to setting this value, ensure that a campaign ID is linked to this number in the respective provider. Failure to do so may cause disruption of service, delivery failures, and/or fines.',
              )}
            </Field.HelperText>
          )}
        </Field.Root>
      </Box>
    </Modal>
  )
}
